import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 7.5 visual preview at /preview/lesson/7.5
 * Week 7 capstone: "The shared standard: writing the team's AI ground rules"
 * 8-section format like 6.5. Vocab: Standard.
 * Student ships a one-page AI standard to a real team.
 */

export default function LessonPreviewPage7_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-brief',
  'sec-map',
  'sec-stage-1',
  'sec-stage-2',
  'sec-stage-3',
  'sec-stage-4',
  'sec-forward',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[210px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <Map />
          <Stage1 />
          <Stage2 />
          <Stage3 />
          <Stage4 />
          <CarryForward />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 7 · Working with people who use AI differently · Lesson 7.5 · Capstone
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The shared standard: writing the team\'s AI ground rules
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 60 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Standard · Team · Ship</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        One real team. One one-page standard. Sent today.
      </p>
    </header>
  );
}

/* --- Section 1: Brief --- */
function Brief() {
  return (
    <Section id="sec-brief" kicker="The brief" title="Two teams. Same software. Same week. One has an AI ground rules doc. One does not.">
      <p className="mt-6 text-text/90">
        Two teams use the same AI tools in the same week. They are
        comparable in skill, size, and project type. By Friday, one team is
        shipping calmly. The other is in a slow-burn argument about whose
        AI use is appropriate.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Team A · No standard, low trust
          </p>
          <p className="mt-3 text-text/90 italic">
            Three people use AI heavily. One refuses it. One uses it
            quietly and never says so. Every disagreement is a fresh
            argument because nothing was ever decided. By month two, the
            refuser is being routed around and the quiet user has been
            outed. The team is functional but tense.
          </p>
        </div>
        <div className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            Team B · Has a standard, high trust
          </p>
          <p className="mt-3 text-text/90 italic">
            One page. Five sections. Decided in a 45-minute meeting and
            revisited every six weeks. AI use is named openly. The carve-outs
            for the refuser are written down. Overrides happen quickly because
            everyone knows the rule. No one is hiding anything.
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Assignment
        </p>
        <h3 className="mt-2 text-xl font-semibold text-text leading-snug">
          Draft a one-page AI standard for a real team you are on. Run it
          past at least one teammate. Ship it to the team by end of lesson.
        </h3>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <BriefRow label="What" value="A one-page document, five sections, that names how this team will and will not use AI." />
          <BriefRow label="Team" value="A real one: school project group, club leadership, internship team, sports team, family business, volunteer board." />
          <BriefRow label="Length" value="One page. If it spills to two, you are over-explaining. Cut until it fits." />
          <BriefRow label="Ship" value="Send to one teammate first for a sanity check. Then post to the team channel or hand to a team lead today." />
        </div>
      </div>

      <Subhead n={1} title="Pick the team before you draft anything" className="mt-10" />
      <p className="mt-4 text-text/90">
        The standard only works if the team is real. Pick one team you are
        actually a member of, where there is at least one decision the
        standard would actually affect this month.
      </p>
      <TeamArtifacts />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Before you read on
        </p>
        <p className="mt-3 text-text/90">
          Write down the name of the team. Write down one decision the team
          will face in the next 30 days where AI is part of the picture.
          The rest of the lesson is about turning that into a standard the
          team can use.
        </p>
      </div>
    </Section>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/90">{value}</p>
    </div>
  );
}

const TEAM_ARTIFACTS = [
  {
    n: '01',
    name: 'School project group',
    accent: 'emerald' as const,
    why: 'A group with a shared deliverable and a real deadline. Standard prevents a midnight argument over whose section was AI-drafted.',
  },
  {
    n: '02',
    name: 'Club or student org leadership',
    accent: 'amber' as const,
    why: 'A team that produces public-facing comms (emails, posts, recap docs). Standard decides which of those AI is allowed to touch.',
  },
  {
    n: '03',
    name: 'Internship or first job team',
    accent: 'emerald' as const,
    why: 'A team where you are new and the norms are still being set. A standard you propose is also a signal of how you think.',
  },
  {
    n: '04',
    name: 'Sports team or coaching staff',
    accent: 'amber' as const,
    why: 'A team that uses AI for scheduling, playbook drafts, recruiting outreach, or stats summaries. Standard covers what stays human.',
  },
  {
    n: '05',
    name: 'Family business or side venture',
    accent: 'emerald' as const,
    why: 'Mixed-generation team where AI use varies a lot. Standard is the one document that gets everyone on the same page.',
  },
  {
    n: '06',
    name: 'Volunteer board or nonprofit team',
    accent: 'amber' as const,
    why: 'A team where donor trust and craft matter. Standard names what AI handles vs. what stays in human voice.',
  },
];

function TeamArtifacts() {
  return (
    <div className="mt-6 space-y-3">
      {TEAM_ARTIFACTS.map((a) => {
        const isEm = a.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={a.n} className={`rounded-lg border ${borderCls} ${bgCls} p-4`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">{a.n}</span>
              <h4 className={`text-base font-semibold ${labelCls}`}>{a.name}</h4>
            </div>
            <p className="mt-2 text-sm text-text/85">{a.why}</p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 2: Map --- */
function Map() {
  return (
    <Section id="sec-map" kicker="The map" title="Week 7 was the four moves with one teammate at a time. The standard is all four, written down.">
      <p className="mt-6 text-text/90">
        Tell. Override. Carve-out. Frame. Four lessons, four moves you can run
        on a team. The capstone is the document that bakes those moves
        into how the team works without having to relearn them every
        meeting.
      </p>
      <MapDiagram />
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The Week 7 vocab spine
        </p>
        <p className="mt-3 text-text/90 leading-relaxed">
          <span className="font-mono text-[10px] text-amber mr-2">7.1</span>
          Tell ·
          <span className="font-mono text-[10px] text-amber mx-2">7.2</span>
          Human override ·
          <span className="font-mono text-[10px] text-amber mx-2">7.3</span>
          Carve-out ·
          <span className="font-mono text-[10px] text-amber mx-2">7.4</span>
          Frame ·
          <span className="font-mono text-[10px] text-amber mx-2">7.5</span>
          Standard
        </p>
      </div>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The vocab for the capstone
        </p>
        <p className="mt-3 text-text/90">
          <span className="font-semibold text-text">Standard.</span> A short
          written document that names how a team uses AI: where it is allowed,
          where it is not, who decides edge cases, how disagreements get
          resolved, and when the standard itself gets reviewed. A standard
          is not a policy paper. It is a one-page tool the team actually uses.
        </p>
      </div>
    </Section>
  );
}

const MOVES = [
  {
    n: '01',
    name: '7.1 · Tell',
    accent: 'emerald' as const,
    output: 'Read the small signals in your teammates&rsquo; work. Not to police. To work better.',
  },
  {
    n: '02',
    name: '7.2 · Human override',
    accent: 'amber' as const,
    output: 'Stop bad AI output from shipping without humiliating the teammate who wrote it.',
  },
  {
    n: '03',
    name: '7.3 · Carve-out',
    accent: 'emerald' as const,
    output: 'Work with a teammate who refuses AI without hiding what you do.',
  },
  {
    n: '04',
    name: '7.4 · Frame',
    accent: 'amber' as const,
    output: 'Turn AI disagreements into specific decisions the room can actually make.',
  },
  {
    n: '05',
    name: '7.5 · Standard',
    accent: 'emerald' as const,
    output: 'Write the team&rsquo;s ground rules down so the other four moves stop being ad hoc.',
  },
];

function MapDiagram() {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {MOVES.map((w) => {
        const isEm = w.accent === 'emerald';
        const tagCls = isEm ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber/15 text-amber';
        return (
          <div key={w.n} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-[10px] text-muted">{w.n}</span>
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tagCls}`}
              >
                {w.name}
              </span>
            </div>
            <p className="mt-3 text-sm text-text/85" dangerouslySetInnerHTML={{ __html: w.output }} />
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 3: Stage 1 Map the tells on your team --- */
function Stage1() {
  return (
    <Section id="sec-stage-1" kicker="Stage 1 · Map your team’s tells" title="Before you write the standard, name what is already true about how this team uses AI.">
      <p className="mt-6 text-text/90">
        A standard that ignores how the team already works will be ignored
        right back. Stage 1 is the observational pass: who on the team uses
        AI for what, who refuses, where the silent gaps are. You are not
        building a file; you are making the picture honest before you write
        the rules.
      </p>

      <StageBlock
        label="The move you make on paper"
        accent="emerald"
        body="List every teammate (including yourself). Next to each name, write what you have actually seen of their AI use: heavy, light, refusing, hiding, unclear. For each one, note the tell (or tells) you noticed: vocabulary shift, speed mismatch, “as we discussed,” defense reaction, refusal signal. No tell, no entry; do not guess. Leave the unclear ones unclear. The blanks matter as much as the entries."
      />

      <StageBlock
        label="The prompt you give AI"
        accent="amber"
        body="“Here is what I have observed about how my teammates use AI. Without telling me to confront anyone, what are three patterns this list suggests about the team as a whole? What is one thing I am probably overestimating, and one thing I am probably underestimating?” AI is a second pair of eyes on a picture only you can see. The map stays yours."
      />

      <StageBlock
        label="The reality check"
        accent="emerald"
        body="Take one teammate you have read with the most confidence. Ask yourself: have I actually seen at least two tells in the same direction, or am I running off one? If only one, you do not have a read yet, and the standard should not be built on guesses. Reread Lesson 7.1 if you need to. Mark anyone in your list where the read is thin."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <ul className="mt-3 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            A list of teammates and the tells you have observed for each.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One sentence on the overall team pattern (where the team is on AI today).
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            A list of teammates where your read is still uncertain.
          </li>
        </ul>
      </div>
    </Section>
  );
}

/* --- Section 4: Stage 2 Draft the five sections --- */
function Stage2() {
  return (
    <Section id="sec-stage-2" kicker="Stage 2 · Draft the five sections" title="One page. Five sections. Each one decides a real question the team has had or will have.">
      <p className="mt-6 text-text/90">
        Every team standard worth writing has the same five sections. They
        are short on purpose. The point is not to cover everything; the
        point is to decide the five questions that the team keeps having
        without realizing it.
      </p>

      <StageBlock
        label="Section 01 · Where we use AI"
        accent="emerald"
        body="Name the tasks where AI is the default tool on this team. Examples: meeting notes, drafting first passes of recap emails, summarizing long documents, generating outlines, cleaning up writing. Be specific. “Drafting” is too broad. “Drafting recap emails after weekly client calls” is the right level."
      />

      <StageBlock
        label="Section 02 · Where we do not use AI"
        accent="amber"
        body="Name the tasks where AI is off-limits for this team. Examples: writing in a specific person’s voice without their okay, drafting messages to people who explicitly opted out, work that has to stay in someone’s craft (a thank-you note from the executive director, a college essay, a personal apology). Off-limits means off-limits. No quiet exceptions."
      />

      <StageBlock
        label="Section 03 · How we disclose AI use"
        accent="emerald"
        body="The honesty rule from Lesson 7.3, written down. Examples: “If AI drafted a piece, the author says so in the comment or the doc footer.” “On client-facing work, we tell the client when AI was involved in the draft.” “On internal work, we tell each other when asked, and we never lie about it.” Disclosure rules are the difference between a standard people trust and one they game."
      />

      <StageBlock
        label="Section 04 · How we handle disagreements"
        accent="amber"
        body="The Frame move from Lesson 7.4, baked into the team’s default process. Example language: “When the team disagrees about AI on a specific task, anyone can call a 10-minute frame: what is the specific task, what is the criterion, what is the deadline for the decision. The frame ends with a written one-line decision in the channel.” Without this section, every fight is a fresh fight."
      />

      <StageBlock
        label="Section 05 · How we revisit this"
        accent="emerald"
        body="A standard with no revisit date is a standard that ages into nonsense. Examples: “We reread this in our first standup of every other month.” “Any teammate can propose changes anytime via Slack thread; changes pass with a majority of the team.” “If two teammates flag the same thing twice, we revisit immediately.” The revisit mechanism is what keeps the standard alive."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A one-page draft with all five sections written out. Each section
          is short (3-6 sentences max). Specific examples, not abstractions.
          If you cannot name an actual example in your team for a section,
          that section is too generic; rewrite it with the team in mind.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 5: Stage 3 Pressure-test with one teammate --- */
function Stage3() {
  return (
    <Section id="sec-stage-3" kicker="Stage 3 · Pressure-test with one teammate" title="Standards shipped without a pre-read get ignored. Bring one person in before you bring the team in.">
      <p className="mt-6 text-text/90">
        A standard sent to the full team without anyone else having read it
        first feels like a decree. Even a good one will land badly. The
        pre-read fixes two things at once: catches the lines you got wrong,
        and turns the standard into something at least one other person is
        already standing behind when the team sees it.
      </p>

      <StageBlock
        label="Who to ask"
        accent="amber"
        body="Pick a teammate who is closest to one extreme: either the heaviest AI user or the refuser. If the standard works for the person at the edge, it will hold for everyone in the middle. Avoid asking someone who you know will rubber-stamp it. The point is to find what is broken before the team does."
      />

      <StageBlock
        label="What to ask them"
        accent="emerald"
        body="Three questions, in this order. “Where in this document do you disagree, even slightly?” “Where would you, in your actual work this week, hit an edge case this does not cover?” “What is one sentence you would cut entirely?” Take notes. Do not defend the draft in the moment; just listen and write."
      />

      <StageBlock
        label="The prompt that helps prepare"
        accent="amber"
        body="“Here is my draft AI standard for [team]. Here is what I know about [teammate’s] position on AI. Generate three questions [teammate] is most likely to push back on, and a one-line response I could have ready that does not dismiss the concern. Do not rewrite the standard.” AI rehearses the conversation. You still have it in person."
      />

      <StageBlock
        label="Then revise"
        accent="emerald"
        body="After the conversation, edit the draft. Cut what they cut. Add the edge cases they named. Where they pushed back and you held your ground, keep your line but rewrite the sentence so the reasoning is visible. A standard that shows its work is one the rest of the team will trust faster."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A revised draft, still one page. At least one teammate has read
          it, pushed back, and agreed to back you when you share it with
          the team. The conversation itself is half the value of the
          capstone.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 6: Stage 4 Ship it to the team --- */
function Stage4() {
  return (
    <Section id="sec-stage-4" kicker="Stage 4 · Ship it to the team" title="One channel. One frame. Sent today.">
      <p className="mt-6 text-text/90">
        A standard that lives only in your docs is not a standard. The ship
        for this capstone is the same shape as 5.5 and Week 6: it has to
        actually reach the team, with the right framing, today.
      </p>

      <StageBlock
        label="Pass 1 · The frame pass"
        accent="emerald"
        body="Open the channel where the team actually talks (Slack, group chat, email thread, in-person team meeting agenda). Write a short intro message above the doc: what the standard is, why you drafted it, that you talked to one teammate about it already, and what kind of input you want by when. Three to five sentences. The intro is the cover note for the standard."
      />

      <StageBlock
        label="Pass 2 · The honesty pass"
        accent="amber"
        body="Read the whole standard one more time. Is there anything in it that you would not want the heaviest AI user on the team to read? Anything that would surprise the refuser? Anything you wrote to look balanced rather than to be true? Fix it now. Standards that hide their author’s real position get ignored in week three."
      />

      <StageBlock
        label="Ship"
        accent="emerald"
        body="Post it. Send the link. Drop the doc into the channel. Set a deadline for input (one week is usually right). Mention that you will incorporate changes and post a v2 on a specific date. The whole point of the capstone is that the standard reaches the team you are actually on. The first standard is the hardest; the next revision will be much easier because by then it will be a real document the team has lived with."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A one-page AI standard, posted to the team\'s real channel,
          with a deadline for input and a date for v2. One sentence in your
          notes about what part felt hardest: mapping the tells, drafting
          the five sections, the pre-read conversation, or the actual send.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 7: Carry forward --- */
function CarryForward() {
  return (
    <Section id="sec-forward" kicker="What you carry forward" title="Five habits to keep. Five traps to remember.">
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Habits to keep
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">01</span>
              Read the room before you write the rule. The tells come first.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">02</span>
              Name where AI is and is not used; vagueness is what kills
              standards.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">03</span>
              Always pressure-test with one teammate, especially the one at
              the edge.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">04</span>
              Disclosure is non-negotiable. The honesty rule is what keeps
              the standard alive.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">05</span>
              Revisit on a schedule. A standard with no revisit date will
              age into nonsense.
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Traps to remember
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">01</span>
              Writing a policy paper instead of a one-page tool. Pages two
              and three never get read.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">02</span>
              Skipping the pre-read. Standards dropped on the team cold
              feel like decrees.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">03</span>
              Letting AI write the standard for you. The team will read it
              and know.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">04</span>
              Hiding your own position to seem neutral. The team can tell
              when the document is a dodge.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">05</span>
              Building a standard that ignores the refuser on the team. If
              it does not work for them, it does not work.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The bigger move
        </p>
        <p className="mt-3 text-text/90">
          Seven weeks ago, you started by deciding what you would and would
          not trust AI to do for you. Today, you have done the same thing
          for a team you are actually on. The standard is yours, but it
          lives on a real team and changes how that team works. AI helped
          you draft. AI did not read the room, did not handle the pre-read
          conversation, did not ship the document. You did.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 8: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card · Week 7" title="Screenshot this. Working with people who use AI differently, on one card.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Week 7 · Lock card · Working with people who use AI differently
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          The whole point of Week 7: raise the standard without slowing the
          team. Read tells. Override bad output. Carve out room for refusers. Frame the
          disagreement. Write it down.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <WeekTile n="01" name="Tell" lesson="7.1" body="The small signals in someone’s work that reveal how they actually use AI. Read the artifact." />
          <WeekTile n="02" name="Human override" lesson="7.2" body="Stop bad AI output from shipping without humiliating the teammate. Private, surgical, path forward." />
          <WeekTile n="03" name="Carve-out" lesson="7.3" body="Work with a teammate who refuses AI without hiding what you do. Be honest, respect the line." />
          <WeekTile n="04" name="Frame" lesson="7.4" body="Turn the values fight into a specific question the team can answer this meeting." />
          <WeekTile n="05" name="Standard" lesson="7.5" body="One page, five sections, real team. The four moves above, written down so they stick." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The standard, in five sections
          </p>
          <p className="mt-3 text-text/90 leading-relaxed">
            <span className="font-mono text-[10px] text-amber mr-2">01</span>
            Where we use AI ·
            <span className="font-mono text-[10px] text-amber mx-2">02</span>
            Where we do not ·
            <span className="font-mono text-[10px] text-amber mx-2">03</span>
            How we disclose ·
            <span className="font-mono text-[10px] text-amber mx-2">04</span>
            How we handle disagreements ·
            <span className="font-mono text-[10px] text-amber mx-2">05</span>
            How we revisit this
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          End of Week 7. You are no longer alone with the chain. You have a
          team, a document, and a way to disagree without burning anything
          down. Week 8 is the last move: AI in a world where it has impacted
          everything, and what you commit to for the next year.
        </p>
      </div>
    </Section>
  );
}

function WeekTile({
  n,
  name,
  lesson,
  body,
}: {
  n: string;
  name: string;
  lesson: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="font-mono text-[10px] text-muted">{lesson}</span>
      </div>
      <div className="mt-2">
        <span className="inline-block rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85" dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Week 8 · The final week
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            AI in a world where it has impacted everything
          </h3>
          <p className="mt-3 text-sm text-text/85">
            The last week of the program. Not how to use the tool. What you
            commit to, for yourself, in the year ahead, in a world where AI
            has impacted everything from school to work to the news you
            read. One last artifact. One real commitment.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Stage helper --- */
function StageBlock({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent: 'emerald' | 'amber';
}) {
  const isEm = accent === 'emerald';
  const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`mt-5 rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
        {label}
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'The brief',
    'The map',
    '1 · Map the tells',
    '2 · Draft 5 sections',
    '3 · Pressure-test',
    '4 · Ship to team',
    'Carry forward',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
